<template>
  <div>
    <div
      @contextmenu.prevent="$refs.menu.open"
      :style="`${calculateBorderColor(pathwayColor)} padding: 0.075rem`"
      class="rounded position-relative drop-shadow-default"
    >
      <img class="d-none d-md-flex" style="position: absolute; right: 0; top: 0; width: 100%" :src="pathwayImage" v-if="pathwayImage" />

      <div class="rounded" style="background: #191a22">
        <div
          class="d-flex align-items-center justify-content-between card-body p-0 rounded z-2 position-relative"
          :style="calculateBackgroundColor(pathwayColor)"
        >
          <div class="d-flex align-items-center p-1">
            <b-avatar
              :src="pathwayCohortData.pathway.avatar"
              :text="avatarText(pathwayCohortData.pathway.name)"
              size="80px"
              variant="light-info"
              style="margin-top: 1px"
              class="mr-1"
              rounded
            />
            <div class="d-flex flex-column align-items-start justify-content-center">
              <b-link
                :to="{ name: 'apps-roster-pathway-view', params: { id: pathwayCohortData.pathway.id } }"
                class="d-flex align-items-center text-white"
              >
                <small class="" style="font-size: 1.15rem; line-height: 1.35rem; margin-bottom: 1px">{{
                  pathwayCohortData.pathway ? pathwayCohortData.pathway.name : pathwayCohortData.name
                }}</small>
              </b-link>

              <div class="d-flex align-items-center mb-25">
                <h4 class="font-weight-bolder tracking-tighter mr-50" style="font-size: 1.85rem; line-height: 2.15rem; margin-bottom: 2px">
                  {{ resolvePathwayCohortName(pathwayCohortData) }}
                </h4>
                <feather-icon
                  v-if="$can('roster.cohort.update')"
                  icon="EditIcon"
                  size="13"
                  class="cursor-pointer text-muted"
                  style="margin-bottom: 2px"
                  @click="$emit('update:edit-pathway-cohort-active', true)"
                />
              </div>

              <div class="d-flex align-items-center">
                <div class="d-flex align-items-center">
                  <b-badge class="profile-badge mr-25" :variant="`${resolvePathwayCohortStatusVariant(pathwayCohortData.status)}`">
                    <feather-icon :icon="resolvePathwayCohortStatusIcon(pathwayCohortData.status)" size="12" />
                    {{ title(pathwayCohortData.status) }}
                  </b-badge>
                  <b-badge class="profile-badge" :variant="`light-${resolvePathwayCohortVisibilityVariant(pathwayCohortData.visibility)}`">
                    <feather-icon :icon="resolvePathwayCohortVisibilityIcon(pathwayCohortData.visibility)" size="12" />
                    {{ title(pathwayCohortData.visibility) }}
                  </b-badge>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <vue-context ref="menu" class="mw-8">
      <li>
        <b-link
          v-if="$can('roster.cohort.update')"
          class="d-flex align-items-center justify-content-between"
          @click="$emit('update:edit-pathway-cohort-active', true)"
        >
          <span>Edit</span>
          <feather-icon icon="Edit3Icon" size="15" class="ml-50 text-muted" />
        </b-link>
        <b-link v-if="$can('roster.cohort.delete')" class="d-flex align-items-center justify-content-between" @click="archivePathway">
          <span>Archive</span>
          <feather-icon icon="TrashIcon" size="15" class="ml-50 text-muted" />
        </b-link>
        <b-link
          v-if="false && $can('roster.cohort.update')"
          class="d-flex align-items-center justify-content-between"
          @click="sendOnboarding"
        >
          <span>Send Onboarding</span>
          <feather-icon icon="SendIcon" size="15" class="ml-50 text-muted" />
        </b-link>
      </li>
    </vue-context>
  </div>
</template>

<script>
import { BCard, BButton, BAvatar, BRow, BCol, BBadge, BLink } from 'bootstrap-vue';
import { avatarText } from '@core/utils/filter';
import { title } from '@core/utils/filter';
import { hexToRGB } from '@/libs/utils/color';
import usePathwayCohorts from '../usePathwayCohorts';
import useAppConfig from '@core/app-config/useAppConfig';
import VueContext from 'vue-context';
import store from '@/store';

const { skin } = useAppConfig();

export default {
  components: {
    BLink,
    BCard,
    BButton,
    BRow,
    BCol,
    BAvatar,
    BBadge,
    VueContext,
  },
  props: {
    pathwayCohortData: {
      type: Object,
      required: true,
    },
    editPathwayCohortActive: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  computed: {
    pathwayColor() {
      if (
        this.pathwayCohortData.pathway.metadata &&
        this.pathwayCohortData.pathway.metadata.design &&
        this.pathwayCohortData.pathway.metadata.design.color
      ) {
        return this.pathwayCohortData.pathway.metadata.design.color;
      }
      return '#5b5b5e';
    },
    pathwayImage() {
      if (
        this.pathwayCohortData.pathway.metadata &&
        this.pathwayCohortData.pathway.metadata.design &&
        this.pathwayCohortData.pathway.metadata.design.image
      ) {
        return this.pathwayCohortData.pathway.metadata.design.image;
      }
      return null;
    },
  },
  methods: {
    archivePathway() {
      this.$swal({
        title: 'Are you sure?',
        text: 'Are you sure you want to archive this pathway?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Remove',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(async (result) => {
        if (result.value) {
          store
            .dispatch('rosterStoreModule/updatePathwayCohort', {
              cohortId: this.pathwayCohortData.id,
              pathwayCohortData: { status: 'archived' },
            })
            .then((response) => {
              this.$emit('refetch-data');
            });
        }
      });
    },
    sendOnboarding() {
      this.$swal({
        title: 'Are you sure?',
        text: 'Sending onboarding will send the MyIP onboarding email to all students in this cohort who have not yet onboarded or been invited.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Send Onboarding',
        customClass: {
          title: 'font-weight-bolder',
          htmlContainer: 'text-muted',
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(async (result) => {
        if (result.value) {
          store
            .dispatch('rosterStoreModule/sendOnboarding', {
              id: accountId,
              accountData: {
                status: 'archived',
              },
            })
            .then((response) => {
              this.refetchData();
            });
        }
      });
    },
    calculateBackgroundColor(hex) {
      const rgb = hexToRGB(hex);
      return `background: linear-gradient(200deg, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.35) 0%, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.05) 93.34%)`;
    },
    calculateBorderColor(hex) {
      const rgb = hexToRGB(hex);
      return `background: linear-gradient(190deg, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.9) 0%, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.03) 100%);`;
    },
  },
  metaInfo() {
    const title = this.pathwayCohortData.name ? `${this.pathwayCohortData.name}` : 'View Pathway';
    return {
      title,
    };
  },
  setup() {
    const {
      resolvePathwayCohortStatusVariant,
      resolvePathwayCohortStatusIcon,
      resolvePathwayCohortVisibilityVariant,
      resolvePathwayCohortVisibilityIcon,
      resolvePathwayCohortName,
      resolvePathwayCohortDateMessage,
      resolvePathwayCohortDateMessageVariant,
      resolvePathwayCohortDateMessageIcon,
    } = usePathwayCohorts();
    return {
      avatarText,
      resolvePathwayCohortStatusVariant,
      resolvePathwayCohortStatusIcon,
      resolvePathwayCohortVisibilityIcon,
      resolvePathwayCohortVisibilityVariant,
      resolvePathwayCohortName,
      resolvePathwayCohortDateMessage,
      resolvePathwayCohortDateMessageVariant,
      resolvePathwayCohortDateMessageIcon,
      title,
      skin,
    };
  },
};
</script>

<style></style>
